import { DeleteOutline, Edit, Email, InsertDriveFile, WhatsApp } from "@mui/icons-material";
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, List, ListItem, ListItemIcon, ListItemText, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { documentsAtom, modalNewDeliveryDocument, modalTemplate, templateEditAtom } from "../../../atoms/atom";
import { DocumentsService } from "../../../services/DocumentService";
import { DeliveryDocumentContactsDTO } from "../../../types/DeliveryDocumentContactsDTO";
import { DeliveryDocumentDTO } from "../../../types/DeliveryDocumentDTO";
import { DeliveryDocumentsByCompanyDTO } from "../../../types/DeliveryDocumentsByCompanyDTO";
import { TemplateDTO } from "../../../types/TemplateDTO";
import { useAlert } from "../../contextProviders/AlertProvider";
import Dica from "../../tips/Dica";
import "./CreateDeliveryModal.scss";

interface CreateDeliveryProps {
	company: DeliveryDocumentsByCompanyDTO;
	dto: DeliveryDocumentDTO;
	edit: boolean;
	existingFiles?: { fileName: string; originalName: string }[];
	onSubmit?: (form: FormData, sent: boolean) => Promise<boolean>;
}

const CreateDeliveryModal: FC<CreateDeliveryProps> = (props: CreateDeliveryProps) => {
	const { id } = useParams<{ id: string }>();
	const [open, setOpen] = useAtom(modalNewDeliveryDocument);
	const [openTemplate, setOpenTemplate] = useAtom(modalTemplate);
	const [, setTemplateEdit] = useAtom(templateEditAtom);
	const [documents] = useAtom(documentsAtom);
	const [newDelivery, setNewDelivery] = useState({} as DeliveryDocumentDTO);
	const [contacts, setContacts] = useState({} as DeliveryDocumentContactsDTO);
	const [templates, setTemplates] = useState<TemplateDTO[]>([]);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [dragging, setDragging] = useState(false);
	const [loading, setLoading] = useState(false);
	const service = new DocumentsService();
	const { showAlert, showError } = useAlert();

	const editingPending = props.edit && !props.dto.sent;
	const filesDisabled = props.edit && !editingPending;
	const attachedFiles = props.existingFiles
		? props.existingFiles.map(file => file.originalName || file.fileName)
		: editingPending && props.dto.documentUuid
			? props.dto.documentUuid.split(",")
			: [];

	const disabledCreated = !newDelivery.channel || !newDelivery.destinatary || !newDelivery.deliveryType
		|| (!props.edit && !selectedFiles.length && !props.existingFiles?.length);

	const filteredContacts = contacts.whatsapp
		? newDelivery.channel === 'AMBOS'
			? [...contacts.whatsapp, ...contacts.email]
			: newDelivery.channel === 'WHATSAPP'
				? contacts.whatsapp
				: contacts.email
		: [];

	const fileName = filesDisabled ? props.dto.documentUuid : "Adicionar arquivo";

	const hasDeliveryToday = documents?.companies
		.find(company => company.companyId === props.company.companyId)?.documents.some(delivery => {
		const isSameDay = (date1: string, date2: string) => {
			const d1 = new Date(date1);
			const d2 = new Date(date2);
			return d1.getFullYear() === d2.getFullYear() &&
				d1.getMonth() === d2.getMonth() &&
				d1.getDate() === d2.getDate();
		};
		return delivery.destinatary === newDelivery.destinatary &&
			delivery.deliveryType === newDelivery.deliveryType &&
			isSameDay(delivery.createdAt, new Date().toISOString());
	});

	const handleChange = (field: keyof DeliveryDocumentDTO, value: string | boolean) => {
		if (field === 'message' && props.edit) {
			const removedLink = (props.dto.documentUuid || '').split(",")
				.some(uuid => !String(value).includes(`https://interno.inovação.seg.br/api/documents/view/${uuid}`));
			if (removedLink) {
				return;
			}
		}
		setNewDelivery(prev => ({
		...prev,
		[field]: value
		}));
	};

	const handleClose = () => {
		setNewDelivery({} as DeliveryDocumentDTO);
		setSelectedFiles([]);
		setDragging(false);
		setOpen(false);
	};

	const handleRemoveFile = (indexToRemove: number) => {
		setSelectedFiles((prevFiles) =>
			prevFiles.filter((_, index) => index !== indexToRemove)
		);
	};

	const handleAddFiles = (files: File[]) => {
		if (!files.length) {
			return;
		}
		setSelectedFiles(prev => [...prev, ...files]);
	};

	const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		if (!filesDisabled) {
			setDragging(true);
		}
	};

	const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setDragging(false);
	};

	const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setDragging(false);
		if (filesDisabled) {
			return;
		}
		handleAddFiles(Array.from(event.dataTransfer.files));
	};

	const MenuProps = {
		PaperProps: {
			style: {
				maxHeight: 48 * 4.5 + 8,
				width: 250,
			},
		},
	};

	const selectedContacts = newDelivery.destinatary
		? newDelivery.destinatary.split(', ').filter(Boolean)
		: [];

	useEffect(() => {
		setNewDelivery(props.edit
			? { ...props.dto }
			: {} as DeliveryDocumentDTO
		);
		setSelectedFiles([]);
	}, [props.edit, props.dto]);

	useEffect(() => {
		const loadContacts = async () => {
			const response = await service.getContactsFromCompany(props.company.companyId);
			if (response && response.data) {
				setContacts(response.data);
			}
		};
		loadContacts();
	}, [props.company.companyId]);

	useEffect(() => {
		const loadTemplates = async () => {
			const response = await service.getTemplates();
			if (response && response.data) {
				setTemplates(response.data);
			}
		};
		loadTemplates();
	}, [openTemplate]);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			id='modal-new-delivery'
		>
			<DialogTitle>
				{editingPending
					? "Editar envio pendente"
					: props.edit
						? "Reenviar documento"
						: "Enviar novo documento"
				}</DialogTitle>
			<DialogContent id="modal-new-delivery-content">
				<Box>
					<Typography fontWeight="bold" variant="button">Empresa: </Typography>
					<Typography variant="button">{props.company.companyName}</Typography>
				</Box>
				{hasDeliveryToday &&
					<Dica frase={`Um documento do tipo ${newDelivery.deliveryType} já foi realizado hoje para este destinatário`} />
				}
				<Box className="line">
					<FormControl fullWidth>
						<InputLabel>
							Canal de envio
						</InputLabel>
						<Select
							label='Canal de envio'
							id="delivery-channel"
							value={newDelivery.channel}
							onChange={(e) => {
								handleChange('channel', e.target.value);
								handleChange('destinatary', '');
							}}
						>
							<MenuItem
								key={'WHATSAPP'}
								value={'WHATSAPP'}
							>
								<WhatsApp color='success' sx={{marginRight: "10px"}}/>
								WHATSAPP
							</MenuItem>
							<MenuItem
								key={'EMAIL'}
								value={'EMAIL'}
							>
								<Email color='info' sx={{marginRight: "10px"}}/>
								E-MAIL
							</MenuItem>
							<MenuItem
								key={'AMBOS'}
								value={'AMBOS'}
							>
								Ambos
							</MenuItem>
						</Select>
					</FormControl>
					<FormControl fullWidth>
						<InputLabel>
							Contatos para o envio
						</InputLabel>
						<Select
							label='Contato para o envio'
							id="delivery-receiver"
							multiple
							value={newDelivery && newDelivery.destinatary ? newDelivery.destinatary.split(", ") : []}
							onChange={(e) => {
								const value = e.target.value;
								const joinedValues = typeof value === 'string' ? value : value.join(', ');
								handleChange('destinatary', joinedValues);
							}}
							disabled={!newDelivery.channel}
							renderValue={(selected) => {
								return selected
									.map((key) => key)
									.join(', ');
							}}
							MenuProps={MenuProps}
						>
							{(filteredContacts || []).map((item) => (
								<MenuItem
									key={item}
									value={item}
								>
									<Checkbox checked={selectedContacts.includes(item)} />
									<ListItemText primary={item} />
								</MenuItem>
							))}
							{!filteredContacts?.length
								? <MenuItem
									key={'NONE'}
									value={'NONE'}
									disabled
								>
									Nenhum contato cadastrado
								</MenuItem>
								: <></>
							}
						</Select>
					</FormControl>
				</Box>
				<Box className="line">
					<FormControl fullWidth>
						<InputLabel>
							Tipo de documento
						</InputLabel>
						<Select
							label='Tipo de documento'
							id="delivery-type"
							value={newDelivery.deliveryType}
							disabled={props.edit}
							renderValue={(selected) => selected}
							onChange={(e) => {
								handleChange('deliveryType', e.target.value);
								handleChange('message', templates.find(i => i.title === e.target.value)?.message || "")
							}}
						>
							{templates.map((item) => (
								<MenuItem
									key={item.title}
									value={item.title}
									sx={{display: "flex", justifyContent: "space-between"}}
								>
									{item.title}
									<Tooltip title="Editar template">
										<IconButton
											onClick={(event) => {
												event.stopPropagation();
												setTemplateEdit(item);
												setOpenTemplate(true);
											}}
										>
											<Edit/>
										</IconButton>
									</Tooltip>
								</MenuItem>
							))}
							<MenuItem>
								<Button
									onClick={(event) => {
										event.stopPropagation();
										setOpenTemplate(true);
									}}
								>
									Criar novo template
								</Button>
							</MenuItem>
						</Select>
					</FormControl>
					<Box
						className={dragging ? "drop-area dragging" : "drop-area"}
						onDragOver={handleDragOver}
						onDragEnter={handleDragOver}
						onDragLeave={handleDragLeave}
						onDrop={handleDrop}
					>
						<Button
							variant="contained"
							color="primary"
							component="label"
							fullWidth
							sx={{ height: "51px" }}
							title={dragging ? "Solte os arquivos aqui" : fileName}
							disabled={filesDisabled}
						>
							{dragging
								? "Solte aqui"
								: fileName.length > 22
									? fileName.substring(0, 21) + "..."
									: fileName
							}
							<input
								hidden
								type="file"
								multiple
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									if (!e.target.files) return;
									handleAddFiles(Array.from(e.target.files));
								}}
							/>
						</Button>
					</Box>
				</Box>
				{!filesDisabled &&
					<Typography variant="caption" sx={{ml: 1}}>
						Você também pode arrastar e soltar os arquivos sobre o botão acima
					</Typography>
				}
				{attachedFiles.length > 0 && (
					<Box className="attached-files">
						<Typography variant="subtitle1" sx={{ml: 1}}>
							Arquivos já anexados:
						</Typography>
						<List>
							{attachedFiles.map((file) => (
								<ListItem key={file} className="file-item">
									<ListItemIcon>
										<InsertDriveFile color="action" />
									</ListItemIcon>
									<ListItemText
										primary={file}
										primaryTypographyProps={{
											noWrap: true,
											style: { maxWidth: '80%' }
										}}
									/>
								</ListItem>
							))}
						</List>
					</Box>
				)}
				{selectedFiles.length > 0 && (
					<Box>
						<Typography variant="subtitle1" sx={{ml: 1}}>
							Arquivos selecionados:
						</Typography>
						<List>
							{selectedFiles.map((file, index) => (
								<ListItem
									key={`${file.name}-${index}`}
									secondaryAction={
										<IconButton
											edge="end"
											aria-label="delete"
											title="Remover arquivo"
											onClick={() => handleRemoveFile(index)}
											color="error"
										>
											<DeleteOutline />
										</IconButton>
									}
									className="file-item"
								>
									<ListItemIcon>
										<InsertDriveFile color="action" />
									</ListItemIcon>
									<ListItemText
										primary={file.name}
										primaryTypographyProps={{
											noWrap: true,
											style: { maxWidth: '80%' }
										}}
									/>
								</ListItem>
							))}
						</List>
					</Box>
				)}
				<TextField
					sx={{display: newDelivery.deliveryType ? "" : "none"}}
					label="Template de mensagem"
					helperText="Você pode alterar a mensagem se desejar"
					value={newDelivery.message}
					multiline
					onChange={(e) => handleChange('message', e.target.value)}
				/>
			</DialogContent>
			<DialogActions className="actions-new-document">
				<Button variant="contained" color="error" id="close-new-delivery-button" onClick={handleClose}>
					Fechar
				</Button>
				<Button
					variant="contained"
					id="create-new-delivery-button"
					color="success"
					onClick={() => send(false)}
					sx={{display: props.edit && !editingPending ? "none" : ""}}
					disabled={disabledCreated || loading}
				>
					{loading
						? <CircularProgress sx={{color: "#48a7dbff"}} size={'20px'}/>
						: "Salvar e enviar depois"
					}
				</Button>
				<Button
					variant="contained"
					id="create-new-delivery-button"
					color="success"
					onClick={() => send(true)}
					disabled={disabledCreated || loading}
				>
					{loading
						? <CircularProgress sx={{color: "#48a7dbff"}} size={'20px'}/>
						: props.edit && !editingPending ? "Reenviar" : "Enviar"
					}
				</Button>
			</DialogActions>
		</Dialog>
	);

	async function send(sendNow: boolean) {
		setLoading(true);
		try {
			let response;
			const delivery = {
				...newDelivery,
				companyId: props.company.companyId,
				sent: sendNow
			};
			if (props.onSubmit) {
				const dataToSend = new FormData();
				dataToSend.append("data", JSON.stringify(delivery));
				selectedFiles.forEach((file) => {
					dataToSend.append("files", file);
				});
				response = await props.onSubmit(dataToSend, sendNow) ? { status: 200 } : undefined;
			} else if (props.edit && !editingPending) {
				response = await service.resendDelivery(id, delivery);
			} else {
				const dataToSend = new FormData();
				dataToSend.append("data", JSON.stringify(delivery));
				selectedFiles.forEach((file) => {
					dataToSend.append("files", file);
				});
				response = editingPending
					? await service.updateDelivery(props.dto.id, dataToSend)
					: await service.sendNewDelivery(id, dataToSend);
			}
			if (response && response.status === 200) {
				showAlert(sendNow ? "Mensagem enviada com sucesso!" : "Envio salvo com sucesso!");
				setNewDelivery({} as DeliveryDocumentDTO);
				setSelectedFiles([]);
			}
		} catch (error) {
			showError();
		} finally {
			setLoading(false);
			setOpen(false);
		}
	}
}

export default CreateDeliveryModal;